import {
  ComposedChart,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
  ResponsiveContainer,
  Cell,
  Bar,
} from 'recharts';
import { useDarkMode } from '../../context/DarkModeContext';

function fmt(dateStr) {
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
}

// ── Custom candlestick shape ──────────────────────────────────────────────────
//
// Recharts Bar gives us (x, y, width, height) where y = top of the bar and
// height = bar height in pixels. We compute pixel coords for O/H/L/C ourselves
// using the YAxis domain passed via `background` prop trick — but the cleanest
// approach is to encode all four prices into the dataKey and use a custom shape.
//
// We use a "floating bar" trick: dataKey = [min(open,close), max(open,close)]
// gives the body. We draw the wicks via additional SVG lines inside the shape.

const CandlestickShape = (props) => {
  const { x, width, y, height, payload } = props;

  // y and height describe the body in pixel space (Recharts computes these
  // from the [low, high] array we pass as the bar's value).
  // We need pixel positions for open, close, high, low — reconstruct from
  // the y-scale info embedded in the bar's coordinate system.
  // Since Recharts gives us y = pixel top of [open, close] range and
  // height = pixel height of that range, we compute wick endpoints ourselves.

  const { spot_price: close, spot_open: open, spot_high: high, spot_low: low } = payload;

  // If no OHLC data, draw a thin horizontal line at close
  if (open == null || high == null || low == null) {
    return <line x1={x} x2={x + width} y1={y} y2={y} stroke="#6b7280" strokeWidth={1.5} />;
  }

  const isGreen = close >= open;
  const bodyColor = isGreen ? '#22c55e' : '#ef4444';
  const wickColor = isGreen ? '#16a34a' : '#dc2626';

  // The bar encodes [min(open,close), max(open,close)] — that's exactly the body.
  const bodyTop = y;
  const bodyBottom = y + height;
  const bodyX = x + width * 0.15;
  const bodyWidth = width * 0.7;

  // We need the y positions for high and low.
  // Recharts doesn't pass the scale directly into the shape, but we can derive
  // the pixel-per-unit ratio from the body dimensions and the O/C prices.
  const bodyPriceRange = Math.abs(close - open);
  const bodyPixelRange = height;

  // Guard: if body has zero height (open == close), derive scale from context
  // using a fallback of 1px per unit to avoid division by zero.
  const pixelsPerUnit = bodyPriceRange > 0 ? bodyPixelRange / bodyPriceRange : 1;

  const maxOC = Math.max(open, close);
  const minOC = Math.min(open, close);

  const highPixel = bodyTop - (high - maxOC) * pixelsPerUnit;
  const lowPixel  = bodyBottom + (minOC - low) * pixelsPerUnit;

  const cx = x + width / 2;

  return (
    <g>
      {/* Upper wick: from high down to top of body */}
      <line x1={cx} x2={cx} y1={highPixel} y2={bodyTop} stroke={wickColor} strokeWidth={1.5} />
      {/* Body */}
      <rect
        x={bodyX}
        y={bodyTop}
        width={bodyWidth}
        height={Math.max(height, 1)}
        fill={bodyColor}
        stroke={wickColor}
        strokeWidth={0.5}
        rx={1}
      />
      {/* Lower wick: from bottom of body down to low */}
      <line x1={cx} x2={cx} y1={bodyBottom} y2={lowPixel} stroke={wickColor} strokeWidth={1.5} />
    </g>
  );
};

// ── Custom tooltip ────────────────────────────────────────────────────────────

const CandleTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;
  const d = payload[0]?.payload;
  if (!d) return null;
  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-3 shadow-lg text-xs min-w-[130px]">
      <p className="font-semibold text-gray-700 dark:text-gray-200 mb-1.5">{fmt(label)}</p>
      {d.spot_open != null && (
        <>
          <div className="flex justify-between gap-3"><span className="text-gray-400">Open</span><span className="font-medium">₹{d.spot_open?.toFixed(2)}</span></div>
          <div className="flex justify-between gap-3"><span className="text-gray-400">High</span><span className="font-medium text-green-600">₹{d.spot_high?.toFixed(2)}</span></div>
          <div className="flex justify-between gap-3"><span className="text-gray-400">Low</span><span className="font-medium text-red-500">₹{d.spot_low?.toFixed(2)}</span></div>
        </>
      )}
      <div className="flex justify-between gap-3"><span className="text-gray-400">Close</span><span className="font-medium">₹{d.spot_price?.toFixed(2)}</span></div>
    </div>
  );
};

// ── Main component ────────────────────────────────────────────────────────────

export default function SpotPriceChart({ data, symbol, strike, height = 240, syncId }) {
  const { dark } = useDarkMode();

  if (!data || data.length === 0) return null;

  const hasOHLC = data.some((d) => d.spot_open != null);

  const grid = dark ? '#374151' : '#e5e7eb';
  const axis = dark ? '#6b7280' : '#9ca3af';

  // Build chart data: bar value = [min(open,close), max(open,close)] for the body.
  // YAxis domain must cover the full high-low range.
  const chartData = data.map((d) => ({
    ...d,
    // Recharts Bar with array value = [bottom, top] of bar (floating bar)
    body: d.spot_open != null
      ? [Math.min(d.spot_open, d.spot_price), Math.max(d.spot_open, d.spot_price)]
      : [d.spot_price - 0.5, d.spot_price + 0.5],
  }));

  const allPrices = data.flatMap((d) => [
    d.spot_price, d.spot_open, d.spot_high, d.spot_low,
  ]).filter(Boolean);
  if (strike) allPrices.push(Number(strike));
  const priceMin = Math.min(...allPrices);
  const priceMax = Math.max(...allPrices);
  const pad = (priceMax - priceMin) * 0.05 || 5;
  const domain = [priceMin - pad, priceMax + pad];

  return (
    <ResponsiveContainer width="100%" height={height}>
      <ComposedChart data={chartData} syncId={syncId} margin={{ top: 8, right: 12, bottom: 24, left: 8 }}>
        <CartesianGrid strokeDasharray="3 3" stroke={grid} />
        <XAxis
          dataKey="date"
          tickFormatter={fmt}
          tick={{ fontSize: 10, fill: axis }}
          stroke={axis}
          angle={-30}
          textAnchor="end"
          height={44}
        />
        <YAxis
          tickFormatter={(v) => v >= 1000 ? `₹${(v / 1000).toFixed(1)}k` : `₹${v.toFixed(0)}`}
          tick={{ fontSize: 10, fill: axis }}
          stroke={axis}
          width={52}
          domain={domain}
          scale="linear"
        />
        <Tooltip content={<CandleTooltip />} />
        {strike && (
          <ReferenceLine
            y={Number(strike)}
            stroke="#f59e0b"
            strokeDasharray="5 3"
            strokeWidth={1.5}
            label={{ value: `Strike ₹${strike}`, position: 'insideTopRight', fontSize: 10, fill: '#f59e0b' }}
          />
        )}

        {hasOHLC ? (
          <Bar
            dataKey="body"
            shape={<CandlestickShape />}
            isAnimationActive={false}
            maxBarSize={28}
          >
            {chartData.map((entry, i) => (
              <Cell
                key={i}
                fill={entry.spot_price >= entry.spot_open ? '#22c55e' : '#ef4444'}
              />
            ))}
          </Bar>
        ) : (
          /* Fallback: no OHLC data yet — show close-only bar */
          <Bar dataKey="spot_price" fill="#3b82f6" maxBarSize={12} radius={2} />
        )}
      </ComposedChart>
    </ResponsiveContainer>
  );
}
